import React, { useEffect, useCallback } from 'react';
import {
  Box,
  Flex,
  Text,
  Card,
  SimpleGrid,
  Button,
  Spinner,
} from '@chakra-ui/react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import BackButton from 'components/backButton/BackButton';
import { PiPencil } from 'react-icons/pi';
import { GetLoginUserRegionData } from 'reactRedux/thunks/regionEditThunk/RegionEditThunk';
import { sideBarRoutes } from 'utils/StaticData';
import OnOffBadge from './OnOffBadge';
import { DEDUCTION_FIELDS } from './constants';

const DetailItem = ({ label, children }) => (
  <Flex
    alignItems={{ sm: 'start', md: 'center' }}
    direction={{ sm: 'column', md: 'row' }}
    gap="10px"
    padding="4px 8px"
    borderLeft="1px solid #ccc"
  >
    <Text fontWeight="normal">{label} :</Text>
    <Text as="span" fontWeight="semibold">
      {children}
    </Text>
  </Flex>
);

DetailItem.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
};

const RegionalSettingsDetail = () => {
  const { regionDetailId } = useParams();
  const dispatch = useDispatch();
  const { isLoading, getRegionData } = useSelector(
    (state) => state.RegionEditSlice,
  );
  const { adminDetails } = useSelector((state) => state.authSlice);
  const isAdmin = adminDetails?.role === sideBarRoutes?.Admin;

  const getRegions = useCallback(() => {
    dispatch(GetLoginUserRegionData());
  }, [dispatch]);

  useEffect(() => {
    getRegions();
  }, [getRegions]);

  const region = Array.isArray(getRegionData)
    ? getRegionData.find((r) => String(r.regionId) === String(regionDetailId))
    : null;

  if (isLoading || !region) {
    return (
      <Box pt={{ base: '60px', md: '80px', xl: '80px' }}>
        <BackButton
          fallbackUrl="/admin/regional-settings"
          label="Back to Regional Settings"
        />
        <Flex justify="center" align="center" minH="50vh">
          <Spinner size="xl" color="primary" />
        </Flex>
      </Box>
    );
  }

  return (
    <Box pt={{ base: '60px', md: '80px', xl: '80px' }}>
      <Flex justifyContent="space-between" mb={4} gap="3" flexWrap="wrap">
        <BackButton
          fallbackUrl="/admin/regional-settings"
          label="Back to Regional Settings"
        />
        <Button
          py="10px"
          px="15px"
          fontWeight="semibold"
          borderRadius="8px"
          bg="primary"
          color="white"
          _hover={{ bg: 'primary', opacity: 0.8 }}
          leftIcon={<PiPencil size={20} />}
          onClick={() => {
            window.location.href = `/admin/regional-settings/edit/${region.regionId}`;
          }}
        >
          Edit
        </Button>
      </Flex>

      <Flex direction="column" mb={4}>
        <Card
          w="100%"
          boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
          borderRadius="20px"
          paddingInline="40px"
          paddingBlock="40px"
          overflowX={{ sm: 'scroll', lg: 'hidden' }}
        >
          <Text fontSize="xl" fontWeight="bold" mb={6}>
            {region.regionName} - Region Details
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
            <DetailItem label="Region ID">{region.regionId}</DetailItem>
            <DetailItem label="Region Name">{region.regionName}</DetailItem>
            <DetailItem label="Tax">{region.tax}%</DetailItem>
            <DetailItem label="Lost Charge">{region.lostCharge}</DetailItem>
            {isAdmin && (
              <DetailItem label="Profit Sharing">
                {region.profitShare}%
              </DetailItem>
            )}
            <DetailItem label="Settlement Days">
              {region.settlementDays}
            </DetailItem>
            <DetailItem label="Organization Name">
              {region.orgName || 'N/A'}
            </DetailItem>
            <DetailItem label="Organization Address">
              {region.orgAddress || 'N/A'}
            </DetailItem>
            <DetailItem label="Organization Code">
              {region.orgCode || 'N/A'}
            </DetailItem>
            <DetailItem label="Invoice Generated">
              {region.landmark || 'N/A'}
            </DetailItem>
            <DetailItem label="E-Invoice Fee">{region.eInvoiceFee}</DetailItem>
            <DetailItem label="Timezone">{region.timezone || 'N/A'}</DetailItem>
            <DetailItem label="Currency">
              {region.currency || 'N/A'}
              {region.currencySymbol ? ` (${region.currencySymbol})` : ''}
            </DetailItem>
          </SimpleGrid>
        </Card>
      </Flex>

      <Flex direction="column">
        <Card
          w="100%"
          boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
          borderRadius="20px"
          paddingInline="40px"
          paddingBlock="40px"
          overflowX={{ sm: 'scroll', lg: 'hidden' }}
        >
          <Text fontSize="xl" fontWeight="bold" mb={2}>
            Default Merchant Deductions
          </Text>
          <Text fontSize="sm" color="gray.500" mb={6}>
            Applied to new merchants created in this region. Existing merchants
            are not affected.
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            {DEDUCTION_FIELDS.map((deduction) => (
              <Flex
                key={deduction.field}
                justify="space-between"
                align="center"
                padding="10px 14px"
                borderRadius="10px"
                border="1px solid"
                borderColor="gray.200"
                bg="gray.50"
              >
                <Text fontSize="sm" fontWeight="500" color="gray.700">
                  {deduction.label}
                </Text>
                <OnOffBadge value={Boolean(region[deduction.field])} />
              </Flex>
            ))}
          </SimpleGrid>
        </Card>
      </Flex>
    </Box>
  );
};

export default RegionalSettingsDetail;
